import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, pcs, phrase, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { pad: readonly string[]; bass: string; stab: readonly string[]; arp: readonly string[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  Am: { pad: ['A3', 'C4', 'E4'], bass: 'A2', stab: ['A3', 'C4', 'E4', 'A4'], arp: ['A4', 'C5', 'E5', 'A5'] },
  F: { pad: ['F3', 'A3', 'C4'], bass: 'F2', stab: ['A3', 'C4', 'F4', 'A4'], arp: ['F4', 'A4', 'C5', 'F5'] },
  C: { pad: ['C4', 'E4', 'G4'], bass: 'C2', stab: ['G3', 'C4', 'E4', 'G4'], arp: ['G4', 'C5', 'E5', 'G5'] },
  G: { pad: ['G3', 'B3', 'D4'], bass: 'G2', stab: ['G3', 'B3', 'D4', 'G4'], arp: ['G4', 'B4', 'D5', 'G5'] },
  Em: { pad: ['E3', 'G3', 'B3'], bass: 'E2', stab: ['G3', 'B3', 'E4', 'G4'], arp: ['E4', 'G4', 'B4', 'E5'] },
  Dm: { pad: ['D4', 'F4', 'A4'], bass: 'D2', stab: ['A3', 'D4', 'F4', 'A4'], arp: ['D5', 'F5', 'A5', 'D6'] },
};

const PROGS = [
  ['Am', 'F', 'C', 'G'],
  ['Am', 'F', 'G', 'Em'],
  ['F', 'G', 'Am', 'Am'],
  ['Am', 'Dm', 'F', 'G'],
] as const;

const STABS = ['X..X..X...X..X..', 'X..X..X.X..X..X.', 'X.X..X..X.X..X..'] as const;

const LEAD: Note[][] = [
  [[0, 'A4', 2], [3, 'C5', 1], [4, 'E5', 2], [7, 'D5', 1], [8, 'C5', 2], [10, 'E5', 2], [12, 'A5', 3]],
  [[0, 'A5', 2], [3, 'G5', 1], [4, 'F5', 2], [7, 'E5', 1], [8, 'C5', 2], [10, 'F5', 2], [12, 'A5', 3]],
  [[0, 'G5', 2], [3, 'E5', 1], [4, 'C5', 2], [7, 'E5', 1], [8, 'G5', 2], [10, 'E5', 2], [12, 'C5', 3]],
  [[0, 'D5', 2], [3, 'B4', 1], [4, 'G4', 2], [7, 'B4', 1], [8, 'D5', 2], [10, 'G5', 2], [12, 'B4', 2], [14, 'D5', 2]],
];

const ARP = [0, 1, 2, 3, 2, 1, 0, 1, 2, 3, 2, 1, 0, 2, 1, 3] as const;
const KEYS = [0, 2, -2, 1, -1, 3] as const;
const MINOR = [9, 11, 0, 2, 4, 5, 7] as const;

const PLAN: FormPlan = {
  intro: 8,
  outro: 8,
  body: [
    { name: 'build', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 16, min: 8, rank: 1 },
    { name: 'break', bars: 8, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 16, min: 8, rank: 3 },
  ],
};

function score(seed: string, p: Params): Score {
  const ch = chooser(EDM, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const stabRow = ch.pick(STABS);
  const harmonies = prog.map((c) => [{ at: 0, tones: pcs(c.pad) }]);
  const leadRows = ch.orig ? LEAD : phrase(ch.rng, LEAD.map(cellsOf), harmonies, MINOR, [67, 84], 72);
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const dropAt = find(form, 'drop')!;
  const drop2At = find(form, 'drop2');
  const buildAt = find(form, 'build');
  const brk = find(form, 'break');
  const rng = createRng(`edm-${seed}`);
  const human = (v: number) => v * (0.94 + rng.float() * 0.12);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes });

  const stabPts: [number, number][] = [[g.at(0), 700]];
  const leadPts: [number, number][] = [[g.at(0), 600]];
  if (buildAt !== undefined) stabPts.push([g.at(buildAt), 700]);
  stabPts.push([g.at(dropAt) - s, buildAt === undefined ? 900 : 4500], [g.at(dropAt), 7000]);
  leadPts.push([g.at(dropAt), 6000]);
  if (brk !== undefined && drop2At !== undefined) {
    leadPts.push([g.at(brk), 6000], [g.at(brk) + s, 700], [g.at(drop2At) - s, 5000], [g.at(drop2At), 7500]);
    stabPts.push([g.at(brk), 7000], [g.at(brk) + s, 800], [g.at(drop2At) - s, 4000], [g.at(drop2At), 7500]);
  }
  const autos: Score['autos'] = [
    { v: 'stabLp', points: stabPts },
    { v: 'leadLp', points: leadPts },
  ];

  const arp = (bar: number, c: Chord, vel: number) => {
    ARP.forEach((i, step) => play('pluck', bar, step, tr(c.arp[i]!), s * 0.9, human(vel * (step % 4 === 0 ? 1 : 0.8))));
  };
  const roll = (bar: number, frac: number, last: boolean) => {
    const every = frac < 0.25 ? 4 : frac < 0.5 ? 2 : frac < 0.75 ? 1 : 0.5;
    const stop = last ? 14 : 16;
    for (let st = 0; st < stop; st += every) hit('roll', bar, st, 0.3 + 0.7 * Math.min(1, frac + st / 64));
  };

  for (const b of g.bars()) {
    const loop = cycle(b.bar - intro, 4);
    const c = prog[loop]!;
    const drop = b.name === 'drop' || b.name === 'drop2';

    if (b.name === 'intro') {
      for (const st of [0, 4, 8, 12]) hit('kick', b.bar, st, human(0.72));
      if (b.index >= 1) for (const st of [2, 6, 10, 14]) hit('hatO', b.bar, st, human(0.55));
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.6);
      if (b.index >= b.bars - 2) arp(b.bar, c, 0.6);
    }

    if (b.name === 'build') {
      const frac = b.index / b.bars;
      if (!b.last) for (const st of [0, 4, 8, 12]) hit('kick', b.bar, st, human(0.85));
      roll(b.bar, frac, b.last);
      arp(b.bar, c, 0.65 + 0.3 * frac);
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.65);
      if (b.index >= b.bars / 2) for (const st of [2, 6, 10, 14]) play('bass', b.bar, st, tr(c.bass), s * 1.4, 0.7);
    }

    if (drop) {
      const second = b.name === 'drop2';
      for (const st of [0, 4, 8, 12]) hit('kick', b.bar, st, st === 0 ? 1 : human(0.95));
      hit('clap', b.bar, 4, human(0.95));
      hit('clap', b.bar, 12, human(1));
      for (const st of [2, 6, 10, 14]) {
        hit('hatO', b.bar, st, human(0.8));
        play('bass', b.bar, st, tr(c.bass), s * 1.5, human(1));
      }
      if (second || e >= 0.8) for (const st of [1, 3, 5, 7, 9, 11, 13, 15]) hit('hat', b.bar, st, human(0.45));
      for (const h of hits(stabRow)) chord('stab', b.bar, h.step, c.stab.map(tr), s * 1.6, human(h.vel));
      const leadOn = second ? e >= 0.5 : e >= 0.85 && b.index >= b.bars / 2;
      if (leadOn) for (const [step, note, len, vel] of leadRows[loop]!) play('lead', b.bar, step, tr(note), len * s * 0.92, human(vel ?? 0.9));
      if (b.index % 8 === 0) hit('crash', b.bar, 0, 0.85);
      if (b.last && b.bar + 1 < total - 1) for (const st of [12, 13, 14, 15]) hit('roll', b.bar, st, 0.55 + (st - 12) * 0.12);
    }

    if (b.name === 'break') {
      chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.75);
      arp(b.bar, c, 0.7);
      if (b.index === 0) play('sub', b.bar, 0, tr(c.bass), g.barLength * 0.9, 0.9);
      if (b.index < b.bars - 2) for (const [step, note, len] of leadRows[loop]!) play('lead', b.bar, step, tr(note), len * s * 0.92, 0.6);
      else roll(b.bar, 0.5 + (b.index - (b.bars - 2)) * 0.3, b.last);
    }

    if (b.name === 'outro') {
      if (b.index < b.bars - 1) {
        for (const st of [0, 4, 8, 12]) hit('kick', b.bar, st, human(0.85));
        hit('clap', b.bar, 4, human(0.8));
        hit('clap', b.bar, 12, human(0.8));
        for (const st of [2, 6, 10, 14]) hit('hatO', b.bar, st, human(0.6));
        if (b.index < b.bars / 2) for (const st of [2, 6, 10, 14]) play('bass', b.bar, st, tr(c.bass), s * 1.4, 0.8);
        chord('pad', b.bar, 0, c.pad.map(tr), g.barLength * 0.96, 0.55);
      } else {
        hit('kick', b.bar, 0, 1);
        chord('stab', b.bar, 0, prog[0]!.stab.map(tr), s * 6, 1);
        play('sub', b.bar, 0, tr(prog[0]!.bass), g.barLength, 0.9);
        hit('crash', b.bar, 0, 0.8);
      }
    }
  }

  if (buildAt !== undefined) events.push({ v: 'rise', t: g.at(buildAt), end: g.at(dropAt) - s * 2, bar: buildAt, step: 0, vel: 1 });
  if (brk !== undefined && drop2At !== undefined) {
    const from = Math.max(brk, drop2At - 2);
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2At) - s * 2, bar: from, step: 0, vel: 1 });
  }
  for (const bar of [dropAt, drop2At]) {
    if (bar === undefined) continue;
    hit('impact', bar, 0, 1);
    play('sub', bar, 0, tr(prog[0]!.bass), s * 6, 1);
  }

  return {
    id: trackId(EDM, seed, p),
    template: EDM.id,
    seed,
    params: p,
    title: ch.orig ? EDM.ref.title : titleOf(seed),
    genre: EDM.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -10,
    key,
    form,
    mix: { reverbDecay: 2.8, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.3, drive: 1.4, softness: 1.4, pumpDepth: 0.45, pumpRelease: 0.24 },
    events,
    autos,
    accents: { kick: 'kick', clap: 'snare', stab: 'stab', lead: 'melody', impact: 'drop' },
  };
}

export const EDM: Template = {
  id: 'edm',
  genre: 'edm festival big room',
  bpm: [124, 140],
  ref: { seed: 'palco-principal', title: 'Palco Principal', params: { bpm: 134, bars: 48, energy: 0.85 } },
  score,
};
